import { useParams, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { PageHeader } from "@/components/PageHeader"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Alert } from "@/components/Alert/Alert"
import { useSubscriptionDetail } from "@/hooks/useSubscriptionDetail"
import { client } from "@/api"
import { DatePicker } from "@/components/DatePicker/DatePicker"
import { addDays, format, formatDistanceToNow } from "date-fns"
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  UserGroupIcon,
  CreditCardIcon,
  CalendarIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline"
import {
  getEffectiveStatus,
  getTrialDaysRemaining,
  formatTrialEndDate,
  isExpectedStatusDiscrepancy,
  isExpectedPeriodEndDiscrepancy,
  isTrialActive,
} from "@/utils/subscription-helpers"

/**
 * Safely format a Unix timestamp from Stripe
 * Returns "Not Available" if the timestamp is invalid or null
 */
function formatStripeTimestamp(timestamp: number | null | undefined): string {
  if (!timestamp || typeof timestamp !== "number" || isNaN(timestamp)) {
    return "Not Available"
  }
  try {
    const date = new Date(timestamp * 1000)
    if (isNaN(date.getTime())) {
      return "Invalid Date"
    }
    return date.toLocaleDateString()
  } catch (e) {
    console.error("Error formatting timestamp:", timestamp, e)
    return "Format Error"
  }
}

/**
 * Safely format a Unix timestamp to a full datetime string
 */
function formatStripeDateTime(timestamp: number | null | undefined): string {
  if (!timestamp || typeof timestamp !== "number" || isNaN(timestamp)) {
    return "Not Available"
  }
  try {
    const date = new Date(timestamp * 1000)
    if (isNaN(date.getTime())) {
      return "Invalid Date"
    }
    return date.toLocaleString()
  } catch (e) {
    console.error("Error formatting timestamp:", timestamp, e)
    return "Format Error"
  }
}

/**
 * Component for displaying and managing trial end dates
 * Only visible for trialing subscriptions
 */
function TrialSection({
  subscriptionId,
  currentTrialEndDate,
  trialDaysRemaining,
  onUpdate,
}: {
  subscriptionId: number
  currentTrialEndDate: string
  trialDaysRemaining: number
  onUpdate: () => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [newTrialDate, setNewTrialDate] = useState<Date | undefined>(new Date(currentTrialEndDate))
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const trialEndDateFormatted = format(new Date(currentTrialEndDate), "PPP")

  const handleSave = async () => {
    if (!newTrialDate) return

    setIsSaving(true)
    setSaveError(null)

    try {
      const response = await (client.admin.subscriptions[":id"] as any).$put({
        param: { id: subscriptionId.toString() },
        json: { trial_end_date: newTrialDate.toISOString() },
      })

      if (!response.ok) {
        const error: any = await response.json()
        throw new Error(error.message || "Failed to update trial date")
      }

      setIsEditing(false)
      onUpdate()
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to update trial date")
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setNewTrialDate(new Date(currentTrialEndDate))
    setIsEditing(false)
    setSaveError(null)
  }

  return (
    <Alert variant="default">
      <ClockIcon className="h-5 w-5" />
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-semibold">Trial Period</h4>
            {!isEditing ? (
              <p className="text-sm mt-1">
                Trial ends on {trialEndDateFormatted} ({trialDaysRemaining} days remaining)
              </p>
            ) : (
              <div className="mt-3 space-y-3">
                <div className="flex gap-2 items-start">
                  <DatePicker
                    date={newTrialDate}
                    onSelect={setNewTrialDate}
                    disabled={isSaving}
                    defaultMonth={newTrialDate || addDays(new Date(), 7)}
                    className="flex-1 max-w-xs"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSave}
                      disabled={isSaving || !newTrialDate}
                      variant="default"
                      size="sm"
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                    <Button onClick={handleCancel} disabled={isSaving} variant="outline" size="sm">
                      Cancel
                    </Button>
                  </div>
                </div>
                {saveError && <p className="text-xs text-red-500">{saveError}</p>}
              </div>
            )}
          </div>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
              Change Date
            </Button>
          )}
        </div>
      </div>
    </Alert>
  )
}

export function SubscriptionDetail() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error, refetch } = useSubscriptionDetail(subscriptionId!)

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </Layout>
    )
  }

  const { subscription, stripe, stripeCustomer, discrepancies, discrepancyDetails } = data

  // Calculate effective status and trial info
  const effectiveStatus = getEffectiveStatus(subscription)
  const trialDaysRemaining = getTrialDaysRemaining(subscription.trial_end_date)
  const trialEndDateFormatted = formatTrialEndDate(subscription.trial_end_date)
  const isTrial = isTrialActive(subscription)

  // Filter out expected discrepancies (like DB showing "active" during trial period)
  const realDiscrepancies = {
    ...discrepancies,
    status:
      discrepancies.status &&
      !isExpectedStatusDiscrepancy(
        subscription.status,
        stripe && !("error" in stripe) ? stripe.status : subscription.status,
        subscription.trial_end_date
      ),
    current_period_end:
      discrepancies.current_period_end &&
      !isExpectedPeriodEndDiscrepancy(
        subscription.trial_end_date,
        discrepancyDetails?.current_period_end?.difference_minutes || 0
      ),
  }
  const hasRealDiscrepancies = Object.values(realDiscrepancies).some((d) => d === true)

  const getStatusBadge = (status: string, cancelAtPeriodEnd: boolean | null) => {
    if (cancelAtPeriodEnd) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <XCircleIcon className="h-4 w-4 mr-1.5" />
          Cancelling at Period End
        </span>
      )
    }

    switch (status) {
      case "active":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1.5" />
            Active
          </span>
        )
      case "trial":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-4 w-4 mr-1.5" />
            Trial ({trialDaysRemaining} days left)
          </span>
        )
      case "trialing":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-4 w-4 mr-1.5" />
            Trialing
          </span>
        )
      case "cancelled":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <XCircleIcon className="h-4 w-4 mr-1.5" />
            Cancelled
          </span>
        )
      case "past_due":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1.5" />
            Past Due
          </span>
        )
      case "unpaid":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1.5" />
            Unpaid
          </span>
        )
      default:
        return <span className="text-sm">{status}</span>
    }
  }

  const isOrphaned = subscription.entity_id === null

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={`Subscription #${subscription.id}`}
          description={
            subscription.stripe_subscription_id ? (
              <span>
                Stripe:{" "}
                {/* Monospace so Stripe IDs are unambiguous (capital I vs lowercase l vs 1). */}
                <span className="font-mono">{subscription.stripe_subscription_id}</span>
              </span>
            ) : (
              "No Stripe subscription ID"
            )
          }
          actions={
            <Button variant="outline" onClick={() => navigate("/subscriptions")}>
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Subscriptions
            </Button>
          }
        />

        {/* Trial Information with inline editing */}
        {isTrial && subscription.trial_end_date && (
          <TrialSection
            subscriptionId={subscription.id}
            currentTrialEndDate={subscription.trial_end_date}
            trialDaysRemaining={trialDaysRemaining}
            onUpdate={() => refetch()}
          />
        )}

        {/* Discrepancy Alert - Only show REAL discrepancies */}
        {hasRealDiscrepancies && (
          <Alert variant="warning">
            <ExclamationCircleIcon className="h-5 w-5" />
            <div className="flex-1">
              <h4 className="font-semibold">Sync Issues Detected</h4>
              <p className="text-sm mt-1 mb-3">
                This subscription has discrepancies between our database and Stripe:
              </p>
              <ul className="text-sm space-y-2 list-disc list-inside">
                {discrepancyDetails?.current_period_end && realDiscrepancies.current_period_end && (
                  <li>
                    <strong>Period End Date:</strong> Stripe and database differ by{" "}
                    {discrepancyDetails.current_period_end.difference_minutes} minutes (
                    {discrepancyDetails.current_period_end.difference_seconds} seconds).
                    <div className="ml-6 mt-1 text-xs opacity-80">
                      Stripe:{" "}
                      {new Date(
                        discrepancyDetails.current_period_end.stripe * 1000
                      ).toLocaleString()}{" "}
                      | Database:{" "}
                      {new Date(
                        discrepancyDetails.current_period_end.database * 1000
                      ).toLocaleString()}
                    </div>
                  </li>
                )}
                {discrepancyDetails?.status && realDiscrepancies.status && (
                  <li>
                    <strong>Status:</strong> Stripe shows "{discrepancyDetails.status.stripe}" but
                    database has "{discrepancyDetails.status.database}"
                  </li>
                )}
                {discrepancyDetails?.cancel_at_period_end &&
                  realDiscrepancies.cancel_at_period_end && (
                    <li>
                      <strong>Cancellation Setting:</strong> Stripe shows{" "}
                      {discrepancyDetails.cancel_at_period_end.stripe
                        ? "will cancel"
                        : "won't cancel"}{" "}
                      but database has{" "}
                      {discrepancyDetails.cancel_at_period_end.database
                        ? "will cancel"
                        : "won't cancel"}
                    </li>
                  )}
                {/* Neither direction is harmless: a comp that lost its discount is
                    about to charge someone who was promised free, and a comp we
                    failed to record is revenue we aren't collecting. */}
                {discrepancyDetails?.is_comp && realDiscrepancies.is_comp && (
                  <li>
                    <strong>Comp status:</strong>{" "}
                    {discrepancyDetails.is_comp.stripe
                      ? "Stripe is discounting this 100% but our record does not mark it as a comp — this team is getting the plan free without it being recorded."
                      : "Our record marks this a comp but Stripe is no longer discounting it — this customer is about to be charged."}
                  </li>
                )}
              </ul>
            </div>
          </Alert>
        )}

        {/* Orphaned Subscription Alert */}
        {isOrphaned && (
          <Alert variant="default">
            <ExclamationCircleIcon className="h-5 w-5" />
            <div>
              <h4 className="font-semibold">Orphaned Subscription</h4>
              <p className="text-sm mt-1">
                This subscription was created before the team was established. It will be
                automatically attached when the user creates their team.
              </p>
            </div>
          </Alert>
        )}

        {/* Stripe Error Alert */}
        {stripe && "error" in stripe && (
          <Alert variant="destructive">
            <ExclamationCircleIcon className="h-5 w-5" />
            <div>
              <h4 className="font-semibold">Stripe Data Unavailable</h4>
              <p className="text-sm mt-1">
                {stripe.error}: {stripe.details}
              </p>
            </div>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Database Data */}
          <div className="bg-surface border border-border rounded-lg p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Database Record</h2>
              <CreditCardIcon className="h-5 w-5 text-secondary" />
            </div>

            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="text-sm font-medium text-secondary">Status</label>
                <div className="mt-1">
                  {getStatusBadge(effectiveStatus, subscription.cancel_at_period_end)}
                </div>
                {isTrial && subscription.trial_end_date && (
                  <p className="text-xs text-secondary mt-1">Trial ends: {trialEndDateFormatted}</p>
                )}
                {/* $0 invoices look identical to a healthy paying account in every
                    other field on this page, so name it explicitly. */}
                {subscription.comp_reason && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      Comped — 100% off
                    </span>
                    <p className="text-xs text-secondary mt-1">{subscription.comp_reason}</p>
                  </div>
                )}
              </div>

              {/* Customer — or, on a comp, the admin who granted it. `creator_id`
                  is "who made this subscription exist", which for a comp is not
                  the customer at all; calling it one misreads the audit trail. */}
              <div>
                <label className="text-sm font-medium text-secondary">
                  {subscription.comp_reason ? "Granted by" : "Customer"}
                </label>
                <div className="mt-1">
                  {subscription.creator ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/users/${subscription.creator!.public_id}`)}
                      className="justify-start"
                    >
                      <UserIcon className="h-4 w-4 mr-2" />
                      <div className="text-left">
                        <p className="font-medium">{subscription.creator.name || "Unnamed User"}</p>
                        <p className="text-xs text-secondary">{subscription.creator.email}</p>
                      </div>
                    </Button>
                  ) : (
                    <p className="text-sm text-secondary italic">No creator</p>
                  )}
                </div>
              </div>

              {/* Team */}
              <div>
                <label className="text-sm font-medium text-secondary">Team / Organization</label>
                <div className="mt-1">
                  {subscription.organization_public_id ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/organizations/${subscription.organization_public_id}`)
                      }
                    >
                      {subscription.organization_name}
                    </Button>
                  ) : subscription.team ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/teams/${subscription.team!.public_id}`)}
                      className="justify-start"
                    >
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      <div className="text-left">
                        <p className="font-medium">{subscription.team.name}</p>
                        <p className="text-xs text-secondary">{subscription.team.public_id}</p>
                      </div>
                    </Button>
                  ) : isOrphaned ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded text-sm font-medium bg-yellow-50 text-yellow-700">
                      Pending Team Creation
                    </span>
                  ) : (
                    <p className="text-sm text-secondary italic">No team</p>
                  )}
                </div>
              </div>

              {/* Plan */}
              {subscription.plan && (
                <div>
                  <label className="text-sm font-medium text-secondary">Plan</label>
                  <div className="mt-1 p-3 bg-background rounded border border-border">
                    <p className="font-medium">{subscription.plan.name}</p>
                    <p className="text-xs text-secondary mt-1 capitalize">
                      {subscription.plan.tier} tier
                    </p>
                    {subscription.plan.max_games !== null && (
                      <p className="text-xs text-secondary mt-1">
                        {subscription.plan.max_games} games
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Billing Period */}
              <div>
                <label className="text-sm font-medium text-secondary">Current Period</label>
                <div className="mt-1 space-y-1 text-sm">
                  {subscription.current_period_start && (
                    <div className="flex items-center justify-between">
                      <span className="text-secondary">Start:</span>
                      <span>
                        {new Date(subscription.current_period_start).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {subscription.current_period_end && (
                    <div className="flex items-center justify-between">
                      <span className="text-secondary">End:</span>
                      <span
                        className={
                          realDiscrepancies?.current_period_end
                            ? "text-orange-600 font-semibold"
                            : ""
                        }
                      >
                        {new Date(subscription.current_period_end).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Cancellation */}
              {subscription.cancellation_reason && (
                <div>
                  <label className="text-sm font-medium text-secondary">Cancellation</label>
                  <div className="mt-1 p-3 bg-background rounded border border-border">
                    <p className="text-sm font-medium capitalize">
                      {subscription.cancellation_reason.replace(/_/g, " ")}
                    </p>
                    {subscription.cancelled_at && (
                      <p className="text-xs text-secondary mt-1">
                        {new Date(subscription.cancelled_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <label className="text-sm font-medium text-secondary">Timestamps</label>
                <div className="mt-1 space-y-1 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-secondary">Created:</span>
                    <span>
                      {formatDistanceToNow(new Date(subscription.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-secondary">Updated:</span>
                    <span>
                      {formatDistanceToNow(new Date(subscription.updated_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stripe Data */}
          <div className="bg-surface border border-border rounded-lg p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Stripe Data</h2>
              <img
                src="https://stripe.com/img/v3/home/social.png"
                alt="Stripe"
                className="h-5 w-auto opacity-50"
              />
            </div>

            {!subscription.stripe_subscription_id ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-secondary text-center">
                  No Stripe subscription ID
                  <br />
                  <span className="text-sm">
                    This subscription hasn't been synced with Stripe yet
                  </span>
                </p>
              </div>
            ) : stripe && !("error" in stripe) ? (
              <div className="space-y-4">
                {/* Status Comparison */}
                <div>
                  <label className="text-sm font-medium text-secondary">Status</label>
                  <div className="mt-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm capitalize">{stripe.status}</span>
                      {realDiscrepancies?.status && (
                        <span className="text-xs text-orange-600 font-semibold">Mismatch!</span>
                      )}
                    </div>
                    {!realDiscrepancies?.status && stripe.status !== subscription.status && (
                      <p className="text-xs text-secondary mt-1">
                        Note: Status differences during trial periods are normal
                      </p>
                    )}
                  </div>
                </div>

                {/* Customer */}
                {stripeCustomer && (
                  <div>
                    <label className="text-sm font-medium text-secondary">Customer</label>
                    <div className="mt-1 p-3 bg-background rounded border border-border">
                      <p className="text-sm font-medium">{stripeCustomer.name || "Unnamed"}</p>
                      <p className="text-xs text-secondary mt-1">
                        {stripeCustomer.email || "No email"}
                      </p>
                      <p className="text-xs text-secondary mt-1 font-mono">{stripeCustomer.id}</p>
                    </div>
                  </div>
                )}

                {/* Current Period */}
                <div>
                  <label className="text-sm font-medium text-secondary">Current Period</label>
                  <div className="mt-1 space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-secondary">Start:</span>
                      <span>{formatStripeTimestamp(stripe.current_period_start)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary">End:</span>
                      <span
                        className={
                          realDiscrepancies?.current_period_end
                            ? "text-orange-600 font-semibold"
                            : ""
                        }
                      >
                        {formatStripeTimestamp(stripe.current_period_end)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cancel Settings */}
                <div>
                  <label className="text-sm font-medium text-secondary">
                    Cancellation Settings
                  </label>
                  <div className="mt-1 space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-secondary">Cancel at period end:</span>
                      <span
                        className={
                          realDiscrepancies?.cancel_at_period_end
                            ? "text-orange-600 font-semibold"
                            : ""
                        }
                      >
                        {stripe.cancel_at_period_end ? "Yes" : "No"}
                      </span>
                    </div>
                    {stripe.canceled_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-secondary">Canceled at:</span>
                        <span>{formatStripeDateTime(stripe.canceled_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Trial */}
                {stripe.trial_end && (
                  <div>
                    <label className="text-sm font-medium text-secondary">Trial End</label>
                    <div className="mt-1">
                      <CalendarIcon className="h-4 w-4 inline mr-2 text-secondary" />
                      <span className="text-sm">{formatStripeTimestamp(stripe.trial_end)}</span>
                    </div>
                  </div>
                )}

                {/* Direct Link */}
                <div className="pt-4 border-t border-border">
                  <a
                    href={`https://dashboard.stripe.com/subscriptions/${subscription.stripe_subscription_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    View in Stripe Dashboard →
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-secondary text-center">Unable to load Stripe data</p>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Overrides - Admin Only */}
        {subscription.team && (
          <SubscriptionOverrideForm
            teamPublicId={subscription.team.public_id}
            trialEndDate={subscription.trial_end_date}
          />
        )}
      </div>
    </Layout>
  )
}

/**
 * Subscription Override Form Component
 * Manages pricing, trial, and limit overrides for team subscriptions
 */
interface SubscriptionOverrideFormProps {
  teamPublicId: string
  trialEndDate: string | null
}

type OverrideData = {
  id?: number
  plan_tier: "individual" | "team" | "organization"
  custom_price_cents?: number | null
  max_games_override?: number | null
  max_storage_gb_override?: number | null
  max_team_members_override?: number | null
  admin_notes?: string | null
}

function SubscriptionOverrideForm({ teamPublicId, trialEndDate }: SubscriptionOverrideFormProps) {
  const [overrides, setOverrides] = useState<OverrideData[]>([])
  const [selectedPlanTier, setSelectedPlanTier] = useState<"individual" | "team" | "organization">(
    "individual"
  )
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{
    variant: "success" | "destructive"
    text: string
  } | null>(null)
  const [formData, setFormData] = useState<Partial<OverrideData>>({})
  const [showForm, setShowForm] = useState(false)

  // Determine if subscription is on trial (price editing allowed only during trial)
  const isOnTrial = isTrialActive({ trial_end_date: trialEndDate } as any)

  // Get override for currently selected plan tier
  const currentOverride = overrides.find((o) => o.plan_tier === selectedPlanTier) || null

  // Fetch existing overrides on mount
  useEffect(() => {
    async function fetchOverrides() {
      try {
        const response = await client.admin["subscription-overrides"].team[":teamPublicId"].$get({
          param: { teamPublicId },
        })

        if (!response.ok) {
          throw new Error("Failed to fetch overrides")
        }

        const data = await response.json()
        // Backend now returns { overrides: [] }
        if (data.overrides && data.overrides.length > 0) {
          setOverrides(data.overrides)
        }
      } catch (error) {
        console.error("Error fetching overrides:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchOverrides()
  }, [teamPublicId])

  // Update form data when selected plan tier changes
  useEffect(() => {
    if (currentOverride) {
      setFormData(currentOverride)
      setShowForm(true)
    } else {
      setFormData({})
      setShowForm(false)
    }
  }, [currentOverride, selectedPlanTier])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setMessage(null)

    try {
      if (currentOverride?.id) {
        // Update existing override
        const response = await client.admin["subscription-overrides"][":id"].$put({
          param: { id: currentOverride.id.toString() },
          json: {
            custom_price_cents: formData.custom_price_cents ?? null,
            max_games_override: formData.max_games_override ?? null,
            max_storage_gb_override: formData.max_storage_gb_override ?? null,
            max_team_members_override: formData.max_team_members_override ?? null,
            admin_notes: formData.admin_notes ?? null,
          },
        })

        if (!response.ok) {
          const error: any = await response.json()
          throw new Error(error?.message || "Failed to update override")
        }

        const data = await response.json()
        // Update overrides array with the updated override
        setOverrides((prev) => prev.map((o) => (o.id === data.override.id ? data.override : o)))
        setMessage({
          variant: "success",
          text: `Override updated successfully for ${selectedPlanTier} plan`,
        })
      } else {
        // Create new override for selected plan tier
        const response = await client.admin["subscription-overrides"].$post({
          json: {
            teamPublicId,
            plan_tier: selectedPlanTier,
            custom_price_cents: formData.custom_price_cents ?? null,
            max_games_override: formData.max_games_override ?? null,
            max_storage_gb_override: formData.max_storage_gb_override ?? null,
            max_team_members_override: formData.max_team_members_override ?? null,
            admin_notes: formData.admin_notes ?? null,
          },
        })

        if (!response.ok) {
          const error: any = await response.json()
          throw new Error(error?.message || "Failed to create override")
        }

        const data = await response.json()
        // Add new override to array
        setOverrides((prev) => [...prev, data.override])
        setMessage({
          variant: "success",
          text: `Override created successfully for ${selectedPlanTier} plan`,
        })
      }
    } catch (error) {
      console.error("Error saving override:", error)
      setMessage({
        variant: "destructive",
        text: error instanceof Error ? error.message : "Failed to save override",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (
      !currentOverride?.id ||
      !confirm(`Are you sure you want to delete the ${selectedPlanTier} plan override?`)
    ) {
      return
    }

    setIsSaving(true)
    setMessage(null)

    try {
      const response = await client.admin["subscription-overrides"][":id"].$delete({
        param: { id: currentOverride.id.toString() },
      })

      if (!response.ok) {
        throw new Error("Failed to delete override")
      }

      // Remove deleted override from array
      setOverrides((prev) => prev.filter((o) => o.id !== currentOverride.id))
      setFormData({})
      setShowForm(false)
      setMessage({
        variant: "success",
        text: `Override deleted successfully for ${selectedPlanTier} plan`,
      })
    } catch (error) {
      console.error("Error deleting override:", error)
      setMessage({
        variant: "destructive",
        text: error instanceof Error ? error.message : "Failed to delete override",
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Subscription Overrides</h2>
      </div>

      {/* Plan Tier Selector */}
      <div className="flex gap-2 mb-4 border-b border-border">
        {(["individual", "team", "organization"] as const).map((tier) => {
          const hasOverride = overrides.some((o) => o.plan_tier === tier)
          return (
            <button
              key={tier}
              type="button"
              onClick={() => setSelectedPlanTier(tier)}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                selectedPlanTier === tier
                  ? "border-primary text-primary"
                  : "border-transparent text-secondary hover:text-foreground"
              }`}
            >
              {tier.charAt(0).toUpperCase() + tier.slice(1)}
              {hasOverride && <span className="ml-2 text-xs text-green-400">●</span>}
            </button>
          )
        })}
      </div>

      {message && (
        <Alert variant={message.variant} className="mb-4">
          {message.text}
        </Alert>
      )}

      {/* Show create button if no override exists for selected tier */}
      {!currentOverride && (
        <div className="text-center py-8">
          <p className="text-secondary mb-2">No override for {selectedPlanTier} plan</p>
          <p className="text-xs text-secondary mb-4">
            Limit overrides can be created anytime. Price overrides require an active trial.
          </p>
          <Button type="button" onClick={() => setShowForm(true)}>
            Create {selectedPlanTier.charAt(0).toUpperCase() + selectedPlanTier.slice(1)} Override
          </Button>
        </div>
      )}

      {(showForm || currentOverride) && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Pricing Section - Conditional rendering based on subscription state */}
          {(isOnTrial || currentOverride?.custom_price_cents != null) && (
            <div className="space-y-4">
              <h3 className="text-md font-semibold">Pricing Override</h3>
              <div>
                <label className="block text-sm font-medium mb-2">Custom Price (cents)</label>
                <input
                  type="number"
                  disabled={!isOnTrial || isSaving}
                  value={formData.custom_price_cents ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      custom_price_cents: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="e.g., 2900 for $29.00"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground disabled:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-secondary mt-1">
                  {formData.custom_price_cents
                    ? `$${(formData.custom_price_cents / 100).toFixed(2)}`
                    : !isOnTrial && currentOverride?.custom_price_cents !== null
                      ? "Custom price set at checkout"
                      : "Leave empty to use plan default"}
                </p>
              </div>
            </div>
          )}

          {/* Limit Overrides */}
          <div className="space-y-4">
            <div>
              <h3 className="text-md font-semibold">Limit Overrides</h3>
              <p className="text-xs text-secondary mt-1">
                {isOnTrial
                  ? "These overrides apply after payment."
                  : "Overrides are active and applied to all limit checks."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Max Games</label>
                <input
                  type="number"
                  disabled={isSaving}
                  value={formData.max_games_override ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_games_override: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="Plan default"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Max Storage (GB)</label>
                <input
                  type="number"
                  disabled={isSaving}
                  value={formData.max_storage_gb_override ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_storage_gb_override: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="Plan default"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Max Team Members</label>
                <input
                  type="number"
                  disabled={isSaving}
                  value={formData.max_team_members_override ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_team_members_override: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="Plan default"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Admin Notes</label>
            <textarea
              disabled={isSaving}
              value={formData.admin_notes ?? ""}
              onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value || null })}
              placeholder="Internal notes about this override..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div>
              {currentOverride && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  Delete Override
                </Button>
              )}
            </div>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : currentOverride ? "Update Override" : "Create Override"}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
